import { useCallback, useRef, useState, useEffect } from 'react';
import { AUDIO_CHUNK_MS, AUDIO_INPUT_SAMPLE_RATE } from '@stream-buddy/shared';
import { logger } from '../utils/logger';

const CHUNK_DURATION_MS = AUDIO_CHUNK_MS;
const TARGET_SAMPLE_RATE = AUDIO_INPUT_SAMPLE_RATE;
const TRAILING_BUFFER_MS = 200; // Reduced from 400ms for faster handoff to AI
const DEFAULT_GATE_THRESHOLD = 0.0035; // Softer gate to preserve quiet/opening speech
const CAPTURE_HEALTHCHECK_MS = 3000;
const WORKLET_STALL_TIMEOUT_MS = 4500;

type CaptureLifecycle = 'idle' | 'starting' | 'running' | 'restarting' | 'stopping';
type CaptureGraph = {
  gainNode: GainNode;
  highpass: BiquadFilterNode;
  limiter: DynamicsCompressorNode;
  lowpass: BiquadFilterNode;
  presence: BiquadFilterNode;
  source: MediaStreamAudioSourceNode;
  workletNode: AudioWorkletNode;
};

interface UseAudioCaptureOptions {
  onAudioData: (audioData: ArrayBuffer) => void;
  pttEnabled?: boolean;
  deviceId?: string;
  inputGain?: number;
  trailingBufferMs?: number;
  noiseGateEnabled?: boolean;
  noiseGateThreshold?: number;
}

export function useAudioCapture({
  onAudioData,
  deviceId,
  inputGain = 1.0,
  trailingBufferMs = TRAILING_BUFFER_MS,
  noiseGateEnabled = true,
  noiseGateThreshold = DEFAULT_GATE_THRESHOLD,
}: UseAudioCaptureOptions) {
  const [isCapturing, setIsCapturing] = useState(false);
  // In PTT mode, default to muted until the hotkey is actively held.
  // Hands-free mode stays unmuted by default.
  const pttEnabled = true;
  const [isMuted, setIsMuted] = useState(true);
  const [audioLevel, setAudioLevel] = useState(0);
  const isMutedRef = useRef(true);
  const inputGainRef = useRef(inputGain);
  const trailingBufferMsRef = useRef(trailingBufferMs);
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const highpassRef = useRef<BiquadFilterNode | null>(null);
  const lowpassRef = useRef<BiquadFilterNode | null>(null);
  const presenceRef = useRef<BiquadFilterNode | null>(null);
  const limiterRef = useRef<DynamicsCompressorNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const muteTimeoutRef = useRef<number | null>(null);
  const muteInnerTimeoutRef = useRef<number | null>(null);
  const startCapturePromiseRef = useRef<Promise<void> | null>(null);
  const healthCheckIntervalRef = useRef<number | null>(null);
  const lastWorkletMessageAtRef = useRef<number>(0);
  const captureLifecycleRef = useRef<CaptureLifecycle>('idle');
  const stopRequestedRef = useRef(false);
  const restartCaptureRef = useRef<(() => Promise<void>) | null>(null);
  const smoothedAudioLevelRef = useRef(0);
  const lastAudioLevelUpdateAtRef = useRef(0);
  const captureSampleRateRef = useRef<number>(TARGET_SAMPLE_RATE);

  // Use ref for callback to prevent stale closures in worklet's onmessage
  // This ensures audio always goes through the latest callback after reconnection
  const onAudioDataRef = useRef(onAudioData);
  useEffect(() => {
    onAudioDataRef.current = onAudioData;
  }, [onAudioData]);

  useEffect(() => {
    inputGainRef.current = inputGain;
    // Update GainNode in real-time for smooth gain changes
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = inputGain;
    }
  }, [inputGain]);

  useEffect(() => {
    trailingBufferMsRef.current = trailingBufferMs;
  }, [trailingBufferMs]);

  // PTT mode: only stream audio while explicitly unmuted (held to talk).
  // Hands-free mode: keep streaming continuously.
  useEffect(() => {
    if (muteTimeoutRef.current) {
      clearTimeout(muteTimeoutRef.current);
      muteTimeoutRef.current = null;
    }
    if (muteInnerTimeoutRef.current) {
      clearTimeout(muteInnerTimeoutRef.current);
      muteInnerTimeoutRef.current = null;
    }

    isMutedRef.current = pttEnabled;
    setIsMuted(pttEnabled);
  }, [pttEnabled]);

  // Sync gain when capture starts - ensures correct value even if there was a race condition
  useEffect(() => {
    if (isCapturing && gainNodeRef.current) {
      gainNodeRef.current.gain.value = inputGain;
    }
  }, [isCapturing, inputGain]);

  const clearMuteTimers = useCallback(() => {
    if (muteTimeoutRef.current) {
      clearTimeout(muteTimeoutRef.current);
      muteTimeoutRef.current = null;
    }
    if (muteInnerTimeoutRef.current) {
      clearTimeout(muteInnerTimeoutRef.current);
      muteInnerTimeoutRef.current = null;
    }
  }, []);

  const clearHealthCheck = useCallback(() => {
    if (healthCheckIntervalRef.current) {
      clearInterval(healthCheckIntervalRef.current);
      healthCheckIntervalRef.current = null;
    }
  }, []);

  const resetCaptureIndicators = useCallback(() => {
    setIsCapturing(false);
    setAudioLevel(0);
    smoothedAudioLevelRef.current = 0;
    lastAudioLevelUpdateAtRef.current = 0;
  }, []);

  const canRecoverCapture = useCallback(() => {
    if (stopRequestedRef.current) {
      return false;
    }

    const lifecycle = captureLifecycleRef.current;
    return lifecycle === 'starting' || lifecycle === 'running';
  }, []);

  // Internal cleanup function that doesn't change isCapturing state
  const cleanupCapture = useCallback(async () => {
    clearHealthCheck();
    clearMuteTimers();
    streamRef.current?.getTracks().forEach(track => {
      track.stop();
    });
    streamRef.current = null;
    sourceNodeRef.current?.disconnect();
    sourceNodeRef.current = null;
    highpassRef.current?.disconnect();
    highpassRef.current = null;
    lowpassRef.current?.disconnect();
    lowpassRef.current = null;
    presenceRef.current?.disconnect();
    presenceRef.current = null;
    limiterRef.current?.disconnect();
    limiterRef.current = null;
    gainNodeRef.current?.disconnect();
    gainNodeRef.current = null;
    workletNodeRef.current?.disconnect();
    workletNodeRef.current = null;
    captureSampleRateRef.current = TARGET_SAMPLE_RATE;
    const audioContext = audioContextRef.current;
    if (audioContext) {
      try {
        // Prevent the deliberate close from being treated as an unexpected failure.
        audioContext.onstatechange = null;
        await audioContext.close();
      } catch {
        // Ignore errors from already-closed contexts
      }
    }
    audioContextRef.current = null;
    lastWorkletMessageAtRef.current = 0;
  }, [clearHealthCheck, clearMuteTimers]);

  const recoverCapture = useCallback(async (reason: string) => {
    if (!canRecoverCapture()) {
      return;
    }

    captureLifecycleRef.current = 'restarting';
    console.warn(`[AudioCapture] Recovering audio capture: ${reason}`);

    try {
      await startCapturePromiseRef.current?.catch(() => undefined);
      await cleanupCapture();
      if (!stopRequestedRef.current) {
        await restartCaptureRef.current?.();
      }
    } catch (error) {
      console.error('[AudioCapture] Recovery failed:', error);
      captureLifecycleRef.current = 'idle';
      resetCaptureIndicators();
    } finally {
      if (captureLifecycleRef.current === 'restarting') {
        captureLifecycleRef.current = stopRequestedRef.current ? 'stopping' : 'idle';
      }
    }
  }, [canRecoverCapture, cleanupCapture, resetCaptureIndicators]);

  const bindAudioContextRecovery = useCallback((audioContext: AudioContext) => {
    audioContext.onstatechange = () => {
      const state = audioContext.state;
      if (state === 'suspended') {
        // Browsers can suspend audio work in background tabs; treat that as
        // a temporary pause, not a capture failure.
        lastWorkletMessageAtRef.current = Date.now();
        void audioContext.resume().catch((error) => {
          console.warn('[AudioCapture] AudioContext resume failed:', error);
        });
        return;
      }

      if (state === 'closed' && canRecoverCapture()) {
        void recoverCapture('audio context closed unexpectedly');
      }
    };
  }, [canRecoverCapture, recoverCapture]);

  const startHealthCheck = useCallback(() => {
    clearHealthCheck();
    healthCheckIntervalRef.current = window.setInterval(() => {
      if (stopRequestedRef.current || captureLifecycleRef.current === 'restarting') {
        return;
      }

      const ctx = audioContextRef.current;
      const stream = streamRef.current;
      const workletNodeCurrent = workletNodeRef.current;

      if (!ctx || !stream || !workletNodeCurrent) {
        void recoverCapture('capture graph missing');
        return;
      }

      if (ctx.state === 'suspended') {
        lastWorkletMessageAtRef.current = Date.now();
        void ctx.resume().catch((error) => {
          console.warn('[AudioCapture] Auto-resume failed:', error);
        });
        return;
      }

      if (ctx.state !== 'running') {
        return;
      }

      const hasDeadTrack = stream.getTracks().some((track) => track.readyState === 'ended');
      if (hasDeadTrack) {
        void recoverCapture('input track ended');
        return;
      }

      const stalledForMs = Date.now() - lastWorkletMessageAtRef.current;
      if (lastWorkletMessageAtRef.current > 0 && stalledForMs > WORKLET_STALL_TIMEOUT_MS) {
        void recoverCapture(`capture stalled for ${stalledForMs}ms`);
      }
    }, CAPTURE_HEALTHCHECK_MS);
  }, [clearHealthCheck, recoverCapture]);

  const ensureAudioContextRunning = useCallback(async (audioContext: AudioContext) => {
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }

    let attempts = 0;
    while (audioContext.state !== 'running' && attempts < 10) {
      await new Promise(resolve => setTimeout(resolve, 50));
      attempts++;
    }

    if (audioContext.state !== 'running') {
      throw new Error(`AudioContext not running: ${audioContext.state}`);
    }
  }, []);

  const handleWorkletMessage = useCallback((event: MessageEvent<{ audioData?: Float32Array; flushed?: boolean; rmsLevel?: number; displayRmsLevel?: number }>) => {
    const { audioData, rmsLevel, displayRmsLevel, flushed } = event.data;
    lastWorkletMessageAtRef.current = Date.now();
    // Always update audio level for visual feedback (even when muted)
    const meterRmsLevel = displayRmsLevel ?? rmsLevel;
    if (meterRmsLevel !== undefined) {
      const rawLevel = Math.min(1, meterRmsLevel * inputGainRef.current * 3.2);
      const previousLevel = smoothedAudioLevelRef.current;
      const smoothing = rawLevel > previousLevel ? 0.5 : 0.2;
      const smoothedLevel = previousLevel + (rawLevel - previousLevel) * smoothing;
      smoothedAudioLevelRef.current = smoothedLevel;

      const now = performance.now();
      if (now - lastAudioLevelUpdateAtRef.current >= 16 || Math.abs(smoothedLevel - previousLevel) >= 0.06 || rawLevel <= 0.01) {
        lastAudioLevelUpdateAtRef.current = now;
        setAudioLevel(smoothedLevel);
      }
    }

    // Stream only while unmuted, except for explicit flush packets.
    // Gain is now applied in the audio graph via GainNode (no JS clipping needed)
    if (audioData && (!isMutedRef.current || flushed)) {
      const pcmInput = captureSampleRateRef.current === TARGET_SAMPLE_RATE
        ? audioData
        : resampleFloat32(audioData, captureSampleRateRef.current, TARGET_SAMPLE_RATE);
      const int16 = float32ToInt16(pcmInput);
      const buffer = new ArrayBuffer(int16.byteLength);
      new Int16Array(buffer).set(int16);
      // Use ref to always call the latest callback (prevents stale closure)
      onAudioDataRef.current(buffer);
    }
  }, []);

  const assignCaptureGraphRefs = useCallback((graph: CaptureGraph) => {
    sourceNodeRef.current = graph.source;
    highpassRef.current = graph.highpass;
    lowpassRef.current = graph.lowpass;
    presenceRef.current = graph.presence;
    gainNodeRef.current = graph.gainNode;
    limiterRef.current = graph.limiter;
    workletNodeRef.current = graph.workletNode;
  }, []);

  const createCaptureGraph = useCallback(async (audioContext: AudioContext, stream: MediaStream) => {
    await audioContext.audioWorklet.addModule('/worklets/capture-processor.js');

    const workletNode = new AudioWorkletNode(audioContext, 'capture-processor', {
      processorOptions: {
        chunkDurationMs: CHUNK_DURATION_MS,
        gateEnabled: noiseGateEnabled,
        gateThreshold: noiseGateThreshold,
      },
    });
    workletNode.port.onmessage = handleWorkletMessage;

    const source = audioContext.createMediaStreamSource(stream);

    // High-pass filter removes low-frequency rumble without thinning out normal speech too much
    const highpass = audioContext.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.value = 90;
    highpass.Q.value = 0.7;  // Butterworth response - flat passband

    // Low-pass filter removes high-frequency hiss and noise above voice range
    // Cutoff at 8kHz - voice fundamentals and harmonics are below this, hiss is above
    const lowpass = audioContext.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 8000;
    lowpass.Q.value = 0.7;  // Butterworth response

    // Presence boost adds clarity and intelligibility to voice without over-sharpening sibilants
    const presence = audioContext.createBiquadFilter();
    presence.type = 'peaking';
    presence.frequency.value = 3000;
    presence.Q.value = 1.0;   // Moderate width - not too narrow, not too wide
    presence.gain.value = 1.5;

    // GainNode for user input gain control
    const gainNode = audioContext.createGain();
    // Use inputGainRef.current which is always up-to-date via useEffect
    // (even if this function closure is stale)
    gainNode.gain.value = inputGainRef.current;

    // Limiter prevents clipping without crushing normal speech dynamics
    const limiter = audioContext.createDynamicsCompressor();
    limiter.threshold.value = -3;
    limiter.knee.value = 3;
    limiter.ratio.value = 10;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.08;

    // Chain: source -> highpass -> lowpass -> presence -> gain -> limiter -> worklet
    source.connect(highpass);
    highpass.connect(lowpass);
    lowpass.connect(presence);
    presence.connect(gainNode);
    gainNode.connect(limiter);
    limiter.connect(workletNode);

    assignCaptureGraphRefs({
      gainNode,
      highpass,
      limiter,
      lowpass,
      presence,
      source,
      workletNode,
    });
  }, [assignCaptureGraphRefs, handleWorkletMessage, noiseGateEnabled, noiseGateThreshold]);

  const startCapture = useCallback(async () => {
    stopRequestedRef.current = false;
    if (startCapturePromiseRef.current) {
      return startCapturePromiseRef.current;
    }

    captureLifecycleRef.current = audioContextRef.current ? 'restarting' : 'starting';
    const startPromise = (async () => {
    // Clean up any existing capture before starting a new one
    // This prevents duplicate audio pipelines after reconnection
    if (audioContextRef.current) {
      await cleanupCapture();
    }

    try {
      const constraints: MediaStreamConstraints = {
        audio: {
          ...(deviceId && { deviceId: { exact: deviceId } }),
          channelCount: { ideal: 1 },
          // Browser DSP is useful for hands-free, but degrades quality in PTT.
          // AGC disabled - we use a limiter for peak protection
          echoCancellation: !pttEnabled,
          noiseSuppression: !pttEnabled,
          autoGainControl: false,
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      const track = typeof stream.getAudioTracks === 'function'
        ? stream.getAudioTracks()[0]
        : null;
      if (track && typeof track.getSettings === 'function') {
        const settings = track.getSettings();
        logger.log('[AudioCapture] Mic settings:', settings);
        if (settings.channelCount && settings.channelCount !== 1) {
          console.warn('[AudioCapture] Non-mono input detected; downmixing to mono in worklet');
        }
      }

      // Create AudioContext at Gemini's preferred 16kHz input rate so the
      // browser resamples mic input before we packetize PCM.
      const audioContext = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
      captureSampleRateRef.current = audioContext.sampleRate;
      if (audioContext.sampleRate !== TARGET_SAMPLE_RATE) {
        console.warn(
          `[AudioCapture] Requested ${TARGET_SAMPLE_RATE}Hz AudioContext, got ${audioContext.sampleRate}Hz. ` +
          'Outgoing audio may need explicit resampling on this browser.'
        );
      }
      audioContextRef.current = audioContext;
      lastWorkletMessageAtRef.current = Date.now();
      bindAudioContextRecovery(audioContext);
      await ensureAudioContextRunning(audioContext);
      await createCaptureGraph(audioContext, stream);
      startHealthCheck();

      captureLifecycleRef.current = 'running';
      setIsCapturing(true);
    } catch (error) {
      console.error('[AudioCapture] Failed to start:', error);
      await cleanupCapture();
      captureLifecycleRef.current = stopRequestedRef.current ? 'stopping' : 'idle';
      resetCaptureIndicators();
      throw error;
    }
    })();

    startCapturePromiseRef.current = startPromise;
    try {
      await startPromise;
    } finally {
      if (startCapturePromiseRef.current === startPromise) {
        startCapturePromiseRef.current = null;
      }
    }
  }, [bindAudioContextRecovery, cleanupCapture, createCaptureGraph, deviceId, ensureAudioContextRunning, pttEnabled, resetCaptureIndicators, startHealthCheck]);

  useEffect(() => {
    restartCaptureRef.current = startCapture;
  }, [startCapture]);

  const stopCapture = useCallback(async () => {
    stopRequestedRef.current = true;
    captureLifecycleRef.current = 'stopping';
    if (startCapturePromiseRef.current) {
      try {
        await startCapturePromiseRef.current;
      } catch {
        // Best effort: if startup failed, continue cleanup path.
      }
    }
    await cleanupCapture();
    captureLifecycleRef.current = 'idle';
    resetCaptureIndicators();
  }, [cleanupCapture, resetCaptureIndicators]);

  useEffect(() => () => {
    void stopCapture();
  }, [stopCapture]);

  useEffect(() => {
    // Media track constraints are applied when getUserMedia starts.
    // Restart active capture so PTT mode toggles take effect immediately.
    if (!isCapturing) {
      return;
    }
    void startCapture().catch(error => {
      console.error('[AudioCapture] Failed to restart after PTT mode change:', error);
    });
  }, [isCapturing, startCapture]);

  const mute = useCallback(() => {
    clearMuteTimers();
    // TRAILING BUFFER: Continue capturing for TRAILING_BUFFER_MS after PTT release
    // This prevents cutting off the last bit of speech
    // The actual mute happens after the trailing period, followed by a flush
    muteTimeoutRef.current = window.setTimeout(() => {
      // Now flush the worklet buffer to send any remaining audio
      if (workletNodeRef.current) {
        workletNodeRef.current.port.postMessage({ command: 'flush' });
      }
      // Small delay to let flush complete before setting muted state
      muteInnerTimeoutRef.current = window.setTimeout(() => {
        muteTimeoutRef.current = null;
        muteInnerTimeoutRef.current = null;
        isMutedRef.current = true;
        setIsMuted(true);
      }, 50);
    }, trailingBufferMsRef.current);
  }, [clearMuteTimers]);

  const unmute = useCallback(() => {
    // Cancel any pending mute timeout to prevent race condition
    if (muteTimeoutRef.current) {
      window.clearTimeout(muteTimeoutRef.current);
      muteTimeoutRef.current = null;
    }
    if (muteInnerTimeoutRef.current) {
      window.clearTimeout(muteInnerTimeoutRef.current);
      muteInnerTimeoutRef.current = null;
    }
    // Don't clear the buffer - user has likely already started speaking by the time PTT fires
    isMutedRef.current = false;
    setIsMuted(false);
  }, []);

  return { isCapturing, isMuted, audioLevel, startCapture, stopCapture, mute, unmute };
}

function float32ToInt16(float32: Float32Array): Int16Array {
  const buffer = new ArrayBuffer(float32.length * 2);
  const view = new DataView(buffer);
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    const value = s < 0 ? s * 0x8000 : s * 0x7fff;
    view.setInt16(i * 2, value, true);
  }
  return new Int16Array(buffer);
}

export function resampleFloat32(input: Float32Array, fromSampleRate: number, toSampleRate: number): Float32Array {
  if (fromSampleRate === toSampleRate || input.length === 0) {
    return input;
  }

  const outputLength = Math.max(1, Math.round(input.length * toSampleRate / fromSampleRate));
  const output = new Float32Array(outputLength);
  const ratio = fromSampleRate / toSampleRate;

  for (let i = 0; i < outputLength; i++) {
    const sourceIndex = i * ratio;
    const index = Math.floor(sourceIndex);
    const nextIndex = Math.min(index + 1, input.length - 1);
    const fraction = sourceIndex - index;
    output[i] = input[index] + (input[nextIndex] - input[index]) * fraction;
  }

  return output;
}
